import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { joinPath } from '@eventbrite/path-utils';
import {
    BANK_ACCOUNTS,
    CASH_OUT_DAYS_LIMIT,
    FINANCE_SETTINGS,
    ONE_DAY_IN_MILLIS,
    ORGANIZATIONS,
    TAXPAYER_INFO,
} from '../constants';
import { NotificationMessageType } from '../types';
import { CreateLinkValues } from './types';

export const getLocalStorageItemWithExpiry = (key: string): string | null => {
    try {
        const itemJSON = localStorage.getItem(key);

        if (!itemJSON) return null;

        const { expiry, value } = JSON.parse(itemJSON);
        const now = new Date();

        if (now.getTime() > expiry) {
            localStorage.removeItem(key);

            return null;
        }

        return value;
    } catch (err) {
        return null;
    }
};

export const getLocalizedPayoutAmount = (
    locale: string,
    payoutAmount: number,
    currency: string,
) => {
    const formattedLocale = locale?.replace('_', '-');
    const localizedPayoutAmount = payoutAmount?.toLocaleString(
        formattedLocale,
        {
            style: 'currency',
            currency,
        },
    );

    return localizedPayoutAmount;
};

export const isDateOld = (dateInString: string) => {
    const today = new Date().getTime();
    const date = Date.parse(dateInString);
    return date < today;
};

export const getDaysToCashOut = (endDateString: string) => {
    const today = new Date().getTime();
    const endDate = new Date(endDateString).getTime();
    const daysToCashOuInMillis = CASH_OUT_DAYS_LIMIT * ONE_DAY_IN_MILLIS;
    const millisLeftToCashOut = endDate - today + daysToCashOuInMillis;
    const daysLeftToCashOut = Math.ceil(
        millisLeftToCashOut / ONE_DAY_IN_MILLIS,
    );

    return daysLeftToCashOut;
};

export const getNotificationMessage = ({
    currency,
    endDate,
    eventName,
    locale,
    payoutAmount,
}: NotificationMessageType) => {
    const localizedPayoutAmount = getLocalizedPayoutAmount(
        locale,
        payoutAmount,
        currency,
    );
    const daysToCashOut = getDaysToCashOut(endDate);
    const isEventEnded = isDateOld(endDate);

    const title = isEventEnded
        ? gettext(
              '%(daysToCashOut)s days left to receive your %(localizedPayoutAmount)s in event sales',
              { daysToCashOut, localizedPayoutAmount },
          )
        : gettext('You have %(localizedPayoutAmount)s in event sales', {
              localizedPayoutAmount,
          });
    const description = gettext(
        'To receive your funds from %(eventName)s, set up a bank account',
        { eventName },
    );
    return { title, description };
};

export const getLocalizedDate = (
    date: string,
    locale = 'en-US',
    options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    },
) => {
    const formattedLocale = locale?.replace('_', '-');
    const localizedDate = new Date(date).toLocaleDateString(
        formattedLocale,
        options,
    );
    return localizedDate;
};

export const isLaunchFinanceSettingsEnabled = (): boolean => {
    const { launchFinanceSettings } =
        window?.__SERVER_DATA__?.featureFlags || {};
    console.log(launchFinanceSettings);
    return Boolean(launchFinanceSettings);
};

export const hasFinancialInfoUncompleted = (
    isEligibleForMissingFinancialInfoNotification: boolean,
    hasCompletedTaxpayerInformationLocally: boolean,
): boolean =>
    isEligibleForMissingFinancialInfoNotification &&
    !hasCompletedTaxpayerInformationLocally;

export const createNotificationsLink = ({
    country,
    currency,
    hasFinancialInfoMissing = false,
    eventId,
}: CreateLinkValues): string => {
    const redirLink = `/${ORGANIZATIONS}/${FINANCE_SETTINGS}/${TAXPAYER_INFO}`;
    const redirParam = hasFinancialInfoMissing ? `redirUrl=${redirLink}` : '';
    const link = `/${ORGANIZATIONS}/${FINANCE_SETTINGS}/${BANK_ACCOUNTS}?country=${country}&currency=${currency}&eventId=${eventId}&openModal=true&${redirParam}`;
    return link;
};

export const navigateTo = (path: string) => {
    const location = getWindowObject('location') || {};
    const baseUrl = location.origin;
    location.href = joinPath(baseUrl, path);
};
